const API_URL = process.env.REACT_APP_API_URL || '/api';

export type PcrStatus = 'new' | 'internal_review' | 'finalized';

/**
 * PCR status filter options in PCR lifecycle order: `new` → `internal_review` → `finalized`.
 *
 * PCR has its OWN three-value lifecycle (see server `pcr.status.ts`); these are not task stages,
 * so `sortTaskStatuses` / `TASK_STATUS_SEQUENCE` do not apply. Keep this array in lifecycle order —
 * the Status dropdown renders it with `preserveOptionsOrder` semantics (no alphabetical sort).
 */
export const PCR_STATUS_OPTIONS: ReadonlyArray<{ id: PcrStatus; name: string }> = [
  { id: 'new', name: 'New' },
  { id: 'internal_review', name: 'Internal Review' },
  { id: 'finalized', name: 'Finalized' },
];

/** Default PCR list filter: actionable items only (not finalized). */
export const PCR_DEFAULT_LIST_STATUSES: readonly PcrStatus[] = ['new', 'internal_review'] as const;

export function isDefaultPcrListStatusFilter(statuses: PcrStatus[] | undefined): boolean {
  const s = statuses ?? [];
  return (
    s.length === PCR_DEFAULT_LIST_STATUSES.length &&
    PCR_DEFAULT_LIST_STATUSES.every((d) => s.includes(d))
  );
}

export function isPcrStatus(value: string): value is PcrStatus {
  return value === 'new' || value === 'internal_review' || value === 'finalized';
}

/** Map URL/query `status` values to a PCR lifecycle status (canonical values only). */
export function normalizePcrStatusParam(raw: string): PcrStatus | undefined {
  const s = raw.trim();
  if (isPcrStatus(s)) return s;
  return undefined;
}

export function formatPcrStatusLabel(status: string): string {
  const n = normalizePcrStatusParam(status);
  const found = PCR_STATUS_OPTIONS.find((o) => o.id === n);
  return found?.name ?? status.replace(/_/g, ' ');
}
export type PcrReportTypeBucket = 'Requests' | 'Compliments' | 'Complaints' | 'N/A';

export interface PcrSubmission {
  id: string;
  receivedAt: string;
  report_id: string;
  service_area_text: string;
  community_text: string;
  /** WRAP foreman area display name when ingest resolved `resolved_foreman_area_id`. */
  foreman_area_name?: string | null;
  resolved_foreman_area_id?: string | null;
  road_text: string;
  report_type: string;
  activity_type: string;
  user_name: string;
  user_email: string;
  user_phone: string;
  contact_method: string;
  message: string;
  image_url: string;
  wrap_image_url: string;
  is_feedback: boolean;
  status: PcrStatus;
  respondedAt: string | null;
  responseTimeBucket: string | null;
  noContactNote: string | null;
  /** Set after manual promotion to a Work ID. */
  workIdId?: string | null;
  linkedWorkIdentificationId?: string | null;
}

/** GET /pcr/submissions/:id/promote-context */
export interface PcrPromoteContextResponse {
  canPromote: boolean;
  canCloseWithoutWork: boolean;
  reasonBlocked?: string;
  serviceAreaId: number | null;
  filterOptions: PcrFilterOptionsResponse;
  suggestedForemanAreaId: number | null;
  suggestedAssetId: number | null;
  suggestedLatitude: number | null;
  suggestedLongitude: number | null;
  defaultComments: string;
  defaultPriority: string;
  pcr: PcrSubmission;
}

export interface PromotePcrToWorkIdRequest {
  foremanAreaId: number;
  comments: string;
  priority: string;
  assetId: number;
  latitude: number;
  longitude: number;
}

export interface PcrPromoteToWorkIdResult {
  workId: {
    id: number;
    workIdentificationId: string | null;
    createdAt?: string;
    status?: string | null;
  };
  submission: PcrSubmission;
}

/** WRAP / WordPress service area ids used for PCR filters (matches server). */
export const PCR_FILTER_SERVICE_AREA_IDS = [16, 17, 22, 25] as const;

export const PCR_FILTER_SERVICE_AREAS: ReadonlyArray<{ id: number; name: string }> = [
  { id: 16, name: 'Service Area 16' },
  { id: 17, name: 'Service Area 17' },
  { id: 22, name: 'Service Area 22' },
  { id: 25, name: 'Service Area 25' },
];

/** WRAP PCR service area display names (align with server PCR_WRAP_SERVICE_AREAS). */
export const PCR_WRAP_SERVICE_AREAS: ReadonlyArray<{ id: number; name: string }> = [
  { id: 16, name: 'South Cariboo' },
  { id: 17, name: 'Central Cariboo' },
  { id: 22, name: 'North Peace' },
  { id: 25, name: 'Bulkley Nass' },
];

export function getPcrWrapServiceAreaName(serviceAreaId: number): string | undefined {
  return PCR_WRAP_SERVICE_AREAS.find((a) => a.id === serviceAreaId)?.name;
}

export interface PcrFilterAssetOption {
  id: number;
  name: string;
  assetNumber: string | null;
}

export interface PcrForemanAreaOption {
  id: number;
  name: string;
  serviceAreaId: number;
}

export interface PcrFilterOptionsResponse {
  serviceAreas: Array<{ id: number; name: string }>;
  foremanAreas: PcrForemanAreaOption[];
  assets: PcrFilterAssetOption[];
}

export interface PcrListParams {
  from?: string;
  to?: string;
  /** `YYYY-MM` — scopes home pie-chart breakdowns to that calendar month. */
  chartMonth?: string;
  /** Legacy contains filter (used only when serviceAreaId is unset). */
  serviceArea?: string;
  /**
   * WRAP service area id(s) 16 | 17 | 22 | 25. OR filter; omit or empty = all service areas.
   * A single string stays supported for older callers (e.g. the home PCR widgets).
   */
  serviceAreaId?: string | string[];
  /** Foreman area id(s). OR filter; omit or empty = all foreman areas. Single string still supported. */
  foremanAreaId?: string | string[];
  /** Legacy road contains (used only when roadAssetId is unset). */
  road?: string;
  /** Road asset id(s). OR filter; omit or empty = all roads. Single string still supported. */
  roadAssetId?: string | string[];
  /** OR filter; omit or empty = all report types. */
  reportTypeBuckets?: PcrReportTypeBucket[];
  /** OR filter; omit or empty = all statuses. */
  statuses?: PcrStatus[];
  page?: number;
  pageSize?: number;
  sortBy?: 'createdAt' | 'status' | 'serviceArea' | 'roadName';
  sortDir?: 'asc' | 'desc';
  search?: string;
}

export interface PcrListResponse {
  submissions: PcrSubmission[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PcrMetricsByServiceAreaStatusCounts {
  new: number;
  internal_review: number;
  finalized: number;
}

export interface PcrMetricsByServiceAreaSlice {
  serviceAreaId: number;
  name: string;
  count: number;
  byStatus: PcrMetricsByServiceAreaStatusCounts;
}

function normalizePcrMetricsByServiceAreaStatusCounts(
  raw: Partial<PcrMetricsByServiceAreaStatusCounts> | undefined,
): PcrMetricsByServiceAreaStatusCounts {
  return {
    new: raw?.new ?? 0,
    internal_review: raw?.internal_review ?? 0,
    finalized: raw?.finalized ?? 0,
  };
}

function normalizePcrMetricsByServiceAreaSlice(
  raw: Partial<PcrMetricsByServiceAreaSlice>,
): PcrMetricsByServiceAreaSlice {
  const byStatus = normalizePcrMetricsByServiceAreaStatusCounts(raw.byStatus);
  return {
    serviceAreaId: raw.serviceAreaId ?? 0,
    name: raw.name ?? '',
    count: raw.count ?? byStatus.new + byStatus.internal_review + byStatus.finalized,
    byStatus,
  };
}

export interface PcrMetricsByResponseTime {
  lt24: number;
  between24_48: number;
  gt48: number;
  noResponse: number;
}

export interface PcrMetricsResponse {
  new: number;
  internal_review: number;
  finalized: number;
  total: number;
  notActionedOver24h: number;
  byServiceArea: PcrMetricsByServiceAreaSlice[];
  byResponseTime: PcrMetricsByResponseTime | null;
}

/** First and last calendar dates (`YYYY-MM-DD`) for a `YYYY-MM` chart month. */
export function pcrChartMonthDateBounds(chartMonth: string): { from: string; to: string } {
  const [yearStr, monthStr] = chartMonth.split('-');
  const year = parseInt(yearStr ?? '', 10);
  const month = parseInt(monthStr ?? '', 10);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    throw new Error('Invalid chart month: use YYYY-MM');
  }
  const lastDay = new Date(year, month, 0).getDate();
  const mm = String(month).padStart(2, '0');
  return {
    from: `${year}-${mm}-01`,
    to: `${year}-${mm}-${String(lastDay).padStart(2, '0')}`,
  };
}

function toQuery(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '') continue;
    q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

/**
 * Single parser for every "single value or many" PCR filter.
 *
 * Accepts `undefined`, a single string, a comma-separated string, or an array of either, and
 * returns de-duplicated non-empty tokens in first-seen order. An empty result always means
 * "no filter" — never "match nothing".
 */
export function pcrFilterValues(raw: string | string[] | undefined | null): string[] {
  const values = Array.isArray(raw) ? raw : raw == null ? [] : [raw];
  const out: string[] = [];
  for (const value of values) {
    for (const token of String(value).split(',')) {
      const trimmed = token.trim();
      if (trimmed && !out.includes(trimmed)) out.push(trimmed);
    }
  }
  return out;
}

/** Repeated query keys (`serviceAreaId=16&serviceAreaId=17`) — the shape PCR already uses for `status`. */
function appendPcrMultiParam(
  q: URLSearchParams,
  key: string,
  raw: string | string[] | undefined,
): void {
  for (const value of pcrFilterValues(raw)) {
    q.append(key, value);
  }
}

function toQueryWithMultiFilters(
  params: Record<string, string | number | undefined>,
  opts?: {
    serviceAreaId?: string | string[];
    foremanAreaId?: string | string[];
    roadAssetId?: string | string[];
    reportTypeBuckets?: PcrReportTypeBucket[];
    statuses?: PcrStatus[];
  },
): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '') continue;
    q.set(k, String(v));
  }
  appendPcrMultiParam(q, 'serviceAreaId', opts?.serviceAreaId);
  appendPcrMultiParam(q, 'foremanAreaId', opts?.foremanAreaId);
  appendPcrMultiParam(q, 'roadAssetId', opts?.roadAssetId);
  if (opts?.reportTypeBuckets?.length) {
    for (const b of opts.reportTypeBuckets) {
      q.append('reportTypeBucket', b);
    }
  }
  if (opts?.statuses?.length) {
    for (const status of opts.statuses) {
      q.append('status', status);
    }
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

export interface CreatePcrSubmissionRequest {
  report_id?: string;
  known_service_area_id?: number;
  service_area_text?: string;
  community_text?: string;
  road_text?: string;
  report_type?: string;
  activity_type?: string;
  user_name?: string;
  user_email?: string;
  user_phone?: string;
  contact_method?: string;
  message?: string;
  image_url?: string;
  wrap_image_url?: string;
  is_feedback?: boolean;
}

export interface PcrSubmissionHistory {
  id: string;
  pcr_submission_id: string;
  stage: string;
  summary: string;
  comments: string;
  external: boolean;
  user_id: string;
  user_name: string;
  created_at: string;
}

/** Matches JSON stored in `comments` when `_format` is `pcr_note_v1`. */
export interface PcrHistoryNoteStoredV1 {
  _format: 'pcr_note_v1';
  kind?: 'regular' | 'contact';
  body: string;
  callerContactedBy?: string;
  contactedAt?: string;
  contractorsRepresentative?: string;
  isFirstContact?: boolean;
  firstContactAt?: string;
}

export interface PostPcrHistoryCommentPayload {
  note: string;
  kind: 'regular' | 'contact';
  callerContactedBy?: string;
  contactedAt?: string;
  contractorsRepresentative?: string;
  isFirstContact?: boolean;
  firstContactAt?: string;
}

/** Create PCR modal: service areas + next public id preview per area (see GET /api/pcr/create-context). */
export interface PcrCreateContext {
  nextReportNumber: number;
  serviceAreas: { id: number; name: string; code: string | null }[];
  /** Next assigned public id per WRAP PCR service area id (e.g. "16" → PCR1626-00001). */
  nextReportIdPreviewByServiceAreaId?: Record<string, string>;
}

export interface PcrContractAreaOption {
  id: number;
  name: string;
  code: string | null;
  /** `foreman` = no linked contract areas; use foreman id for road search, not a contract area id. */
  kind?: 'contract' | 'foreman';
}

export async function fetchPcrCreateContext(): Promise<PcrCreateContext> {
  const response = await fetch(`${API_URL}/pcr/create-context`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string; details?: string };
    throw new Error(body.error || body.details || `Failed to load PCR form data (${response.status})`);
  }
  return (await response.json()) as PcrCreateContext;
}

export async function fetchPcrContractAreasForServiceArea(serviceAreaId: number): Promise<PcrContractAreaOption[]> {
  const response = await fetch(`${API_URL}/pcr/service-areas/${serviceAreaId}/contract-areas`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string; details?: string };
    throw new Error(body.error || body.details || `Failed to load communities (${response.status})`);
  }
  const data = (await response.json()) as { contractAreas?: PcrContractAreaOption[] };
  return data.contractAreas ?? [];
}

/** Same-origin API URL to stream the image (B2 via server), like Work ID attachment download. */
export function getPcrSubmissionImageUrl(submissionId: string): string {
  const base = process.env.REACT_APP_API_URL || '/api';
  return `${base}/pcr/submissions/${submissionId}/image`;
}

const MAX_PCR_IMAGE_BYTES_CLIENT = 10 * 1024 * 1024;

/** PATCH multipart `image` — same limits as PCR create (10 MB images). */
export async function uploadPcrSubmissionImage(submissionId: string, file: File): Promise<PcrSubmission> {
  if (!file.type.toLowerCase().startsWith('image/')) {
    throw new Error('Please choose an image file (JPEG, PNG, WebP, or GIF).');
  }
  if (file.size > MAX_PCR_IMAGE_BYTES_CLIENT) {
    throw new Error('Image must be 10 MB or smaller.');
  }
  const fd = new FormData();
  fd.append('image', file);
  const response = await fetch(`${API_URL}/pcr/submissions/${submissionId}/image`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { Accept: 'application/json' },
    body: fd,
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string; details?: string };
    throw new Error(body.error || body.details || `Failed to upload photo (${response.status})`);
  }
  const data = (await response.json()) as { submission?: PcrSubmission };
  if (!data.submission) {
    throw new Error('Upload succeeded but no submission was returned.');
  }
  return data.submission;
}

export function formatPcrRoadAssetLabel(a: {
  id: number;
  name: string;
  assetNumber?: string | null;
}): string {
  const left = (a.name || '').trim();
  const right = (a.assetNumber || '').trim();
  if (left && right && left.toLowerCase() !== right.toLowerCase()) {
    return `${left} - ${right}`;
  }
  return left || right || `Asset ${a.id}`;
}

export interface PatchPcrSubmissionRequest {
  status?: PcrStatus;
  service_area_text?: string;
  community_text?: string;
  foremanAreaId?: number;
  roadAssetId?: number;
  road_text?: string;
  report_type?: string;
  activity_type?: string;
  user_name?: string;
  user_email?: string;
  user_phone?: string;
  contact_method?: string;
  message?: string;
  is_feedback?: boolean;
  noContactNote?: string | null;
}

export function normalizePcrReportTypeBucket(value: string | null | undefined): PcrReportTypeBucket {
  const normalized = (value || '').trim().toLowerCase();
  if (normalized === 'requests' || normalized === 'request') return 'Requests';
  if (normalized === 'compliments' || normalized === 'compliment') return 'Compliments';
  if (normalized === 'complaints' || normalized === 'complaint') return 'Complaints';
  return 'N/A';
}

export function formatPcrServiceAreaLabel(idOrName: string | number): string {
  const value = String(idOrName).trim();
  const numeric = value.match(/\b(16|17|22|25)\b/);
  if (numeric) return `Service Area ${numeric[1]}`;
  const v = value.toLowerCase();
  if (v.includes('south') && v.includes('cariboo')) return 'Service Area 16';
  if (v.includes('central') && v.includes('cariboo')) return 'Service Area 17';
  if (v.includes('north') && v.includes('peace')) return 'Service Area 22';
  if (
    v.includes('bulkley') &&
    (v.includes('nass') || v.includes('nechako') || v.includes('nichako'))
  ) {
    return 'Service Area 25';
  }
  return `Service Area ${value}`;
}

export async function fetchPcrFilterOptions(serviceAreaId?: string): Promise<PcrFilterOptionsResponse> {
  const query = serviceAreaId ? toQuery({ serviceAreaId }) : '';
  const response = await fetch(`${API_URL}/pcr/filter-options${query}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Failed to load PCR filter options (${response.status})`);
  }
  return (await response.json()) as PcrFilterOptionsResponse;
}

/** Legacy full list (no filters); prefer fetchPcrSubmissionsPaged. */
export async function fetchPcrSubmissions(): Promise<PcrSubmission[]> {
  const response = await fetch(`${API_URL}/pcr/submissions`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
      requiredPermission?: string;
    };
    if (response.status === 401 || response.status === 403) {
      throw new Error(
        'Sign in with permission submission:view to load PCR submissions from the database. ' +
          'Webhook POSTs to /api/pcr/webhooks/formidable are still accepted and stored.',
      );
    }
    throw new Error(body.error || `Failed to load PCR submissions (${response.status})`);
  }
  const data = (await response.json()) as PcrListResponse;
  return data.submissions ?? [];
}

export async function fetchPcrSubmissionsPaged(params: PcrListParams): Promise<PcrListResponse> {
  const query = toQueryWithMultiFilters(
    {
      from: params.from,
      to: params.to,
      serviceArea: params.serviceArea,
      road: params.road,
      search: params.search,
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 25,
      sortBy: params.sortBy,
      sortDir: params.sortDir,
    },
    {
      serviceAreaId: params.serviceAreaId,
      foremanAreaId: params.foremanAreaId,
      roadAssetId: params.roadAssetId,
      reportTypeBuckets: params.reportTypeBuckets,
      statuses: params.statuses,
    },
  );
  const response = await fetch(`${API_URL}/pcr/submissions${query}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    if (response.status === 401 || response.status === 403) {
      throw new Error(
        'Sign in with permission submission:view to load PCR submissions.',
      );
    }
    throw new Error(body.error || `Failed to load PCR submissions (${response.status})`);
  }
  return (await response.json()) as PcrListResponse;
}

export async function fetchPcrMetrics(params: Omit<PcrListParams, 'page' | 'pageSize' | 'sortBy' | 'sortDir'>): Promise<PcrMetricsResponse> {
  const query = toQueryWithMultiFilters(
    {
      from: params.from,
      to: params.to,
      chartMonth: params.chartMonth,
      serviceArea: params.serviceArea,
      road: params.road,
      search: params.search,
    },
    {
      serviceAreaId: params.serviceAreaId,
      foremanAreaId: params.foremanAreaId,
      roadAssetId: params.roadAssetId,
      reportTypeBuckets: params.reportTypeBuckets,
      statuses: params.statuses,
    },
  );
  const response = await fetch(`${API_URL}/pcr/metrics${query}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Failed to load PCR metrics (${response.status})`);
  }
  const raw = (await response.json()) as Partial<PcrMetricsResponse>;
  return {
    new: raw.new ?? 0,
    internal_review: raw.internal_review ?? 0,
    finalized: raw.finalized ?? 0,
    total: raw.total ?? 0,
    notActionedOver24h: raw.notActionedOver24h ?? 0,
    byServiceArea: Array.isArray(raw.byServiceArea)
      ? raw.byServiceArea.map((slice) => normalizePcrMetricsByServiceAreaSlice(slice))
      : [],
    byResponseTime: raw.byResponseTime ?? null,
  };
}

export async function fetchPcrPromoteContext(submissionId: string): Promise<PcrPromoteContextResponse> {
  const response = await fetch(`${API_URL}/pcr/submissions/${submissionId}/promote-context`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string; details?: string };
    throw new Error(body.error || body.details || `Failed to load promote context (${response.status})`);
  }
  return (await response.json()) as PcrPromoteContextResponse;
}

export async function promotePcrToWorkId(
  submissionId: string,
  payload: PromotePcrToWorkIdRequest,
): Promise<PcrPromoteToWorkIdResult> {
  const response = await fetch(`${API_URL}/pcr/submissions/${submissionId}/promote-to-work-id`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string; details?: string };
    if (response.status === 403) {
      throw new Error(
        body.error ||
          'Permission workId:create plus pcr:finalize is required to promote a PCR.',
      );
    }
    throw new Error(body.error || body.details || `Failed to create Work ID (${response.status})`);
  }
  return (await response.json()) as PcrPromoteToWorkIdResult;
}

export async function closePcrWithoutWorkId(submissionId: string, note?: string): Promise<PcrSubmission> {
  const response = await fetch(`${API_URL}/pcr/submissions/${submissionId}/close-without-work`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ note: note?.trim() || undefined }),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string; details?: string };
    if (response.status === 403) {
      throw new Error(
        body.error || 'Permission pcr:finalize is required to finalize a PCR.',
      );
    }
    throw new Error(body.error || body.details || `Failed to close PCR (${response.status})`);
  }
  const data = (await response.json()) as { submission: PcrSubmission };
  return data.submission;
}

export async function patchPcrSubmissionStatus(
  id: string,
  body: { status: PcrStatus; respondedAt?: string },
): Promise<PcrSubmission> {
  const response = await fetch(`${API_URL}/pcr/submissions/${id}/status`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errBody = (await response.json().catch(() => ({}))) as { error?: string; requiredPermission?: string };
    if (response.status === 403) {
      throw new Error(errBody.error || 'Permission denied to change status (finalizing requires pcr:finalize).');
    }
    throw new Error(errBody.error || `Failed to update status (${response.status})`);
  }
  const data = (await response.json()) as { submission: PcrSubmission };
  return data.submission;
}

export async function patchPcrSubmission(
  id: string,
  body: PatchPcrSubmissionRequest,
): Promise<PcrSubmission> {
  const response = await fetch(`${API_URL}/pcr/submissions/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errBody = (await response.json().catch(() => ({}))) as { error?: string; requiredPermission?: string };
    if (response.status === 403) {
      throw new Error(
        errBody.error ||
          'Permission submission:pcr_update or workId:create required to edit PCR details (finalizing requires pcr:finalize).',
      );
    }
    throw new Error(errBody.error || `Failed to update submission (${response.status})`);
  }
  const data = (await response.json()) as { submission: PcrSubmission };
  return data.submission;
}

/** When true, WRAP shows the “Reply to submitter” action (must match server `PCR_REPLY_ENABLED` for sends). */
export function isPcrReplyUiEnabled(): boolean {
  return (process.env.REACT_APP_PCR_REPLY_ENABLED || '').toLowerCase().trim() === 'true';
}

export interface PcrReplyTemplate {
  id: number;
  name: string;
  subjectPattern: string;
  bodyHtml: string;
}

export interface PcrContactLogEntry {
  id: number;
  createdAt: string;
  actorUserId: number;
  actorName: string;
  recipientEmail: string;
  subject: string;
  templateId: number | null;
  templateName: string | null;
  providerMessageId: string | null;
  providerStatus: string | null;
  bodySnippet: string | null;
}

export interface PcrSendReplyResponse {
  logId: number;
  providerMessageId: string | null;
  respondedAtUpdated: boolean;
}

export async function fetchPcrReplyTemplates(): Promise<PcrReplyTemplate[]> {
  const response = await fetch(`${API_URL}/pcr/reply-templates`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Failed to load reply templates (${response.status})`);
  }
  const data = (await response.json()) as { templates: PcrReplyTemplate[] };
  return data.templates ?? [];
}

export async function fetchPcrContactLog(submissionId: string): Promise<PcrContactLogEntry[]> {
  const response = await fetch(`${API_URL}/pcr/submissions/${submissionId}/contact-log`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Failed to load contact log (${response.status})`);
  }
  const data = (await response.json()) as { entries: PcrContactLogEntry[] };
  return data.entries ?? [];
}

export async function postPcrSubmissionReply(
  submissionId: string,
  body: { subject: string; bodyText: string; templateId: number | null },
): Promise<PcrSendReplyResponse> {
  const response = await fetch(`${API_URL}/pcr/submissions/${submissionId}/reply`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      subject: body.subject,
      bodyText: body.bodyText,
      templateId: body.templateId,
    }),
  });
  if (!response.ok) {
    const errBody = (await response.json().catch(() => ({}))) as {
      error?: string;
      details?: string;
      requiredPermission?: string;
    };
    if (response.status === 403) {
      throw new Error(errBody.error || 'Permission submission:pcr_reply required.');
    }
    const extra = errBody.details ? ` ${errBody.details}` : '';
    throw new Error((errBody.error || `Send failed (${response.status})`) + extra);
  }
  return (await response.json()) as PcrSendReplyResponse;
}

export async function downloadPcrMonthlyCsv(
  month: string,
  filters: Omit<PcrListParams, 'page' | 'pageSize' | 'sortBy' | 'sortDir' | 'status'>,
): Promise<void> {
  // NOTE: statuses are deliberately NOT sent — the monthly CSV covers every status for the month.
  const query = toQueryWithMultiFilters(
    {
      month,
      from: filters.from,
      to: filters.to,
      serviceArea: filters.serviceArea,
      road: filters.road,
    },
    {
      serviceAreaId: filters.serviceAreaId,
      foremanAreaId: filters.foremanAreaId,
      roadAssetId: filters.roadAssetId,
      reportTypeBuckets: filters.reportTypeBuckets,
    },
  );
  const response = await fetch(`${API_URL}/pcr/export/monthly${query}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Export failed (${response.status})`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  let filename = `pcr-monthly-${month}.csv`;
  const m = disposition?.match(/filename="([^"]+)"/);
  if (m?.[1]) filename = m[1];
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function appendPcrFormFields(formData: FormData, payload: CreatePcrSubmissionRequest): void {
  const keys: (keyof CreatePcrSubmissionRequest)[] = [
    'report_id',
    'service_area_text',
    'community_text',
    'road_text',
    'report_type',
    'activity_type',
    'user_name',
    'user_email',
    'user_phone',
    'contact_method',
    'message',
    'image_url',
    'wrap_image_url',
  ];
  for (const key of keys) {
    const value = payload[key];
    if (value === undefined || value === null) {
      formData.append(key, '');
    } else {
      formData.append(key, String(value));
    }
  }
  if (payload.is_feedback !== undefined) {
    formData.append('is_feedback', payload.is_feedback ? 'true' : 'false');
  }
  if (payload.known_service_area_id != null && Number.isFinite(payload.known_service_area_id)) {
    formData.append('known_service_area_id', String(payload.known_service_area_id));
  }
}

/**
 * Create a PCR submission. Pass `imageFile` to upload a photo (multipart); otherwise JSON is used.
 */
export async function createPcrSubmission(
  payload: CreatePcrSubmissionRequest,
  imageFile?: File | null,
): Promise<PcrSubmission> {
  const useMultipart = imageFile instanceof File;

  const response = await fetch(`${API_URL}/pcr/submissions`, {
    method: 'POST',
    credentials: 'include',
    headers: useMultipart
      ? { Accept: 'application/json' }
      : {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
    body: useMultipart
      ? (() => {
          const fd = new FormData();
          appendPcrFormFields(fd, payload);
          fd.append('image', imageFile);
          return fd;
        })()
      : JSON.stringify(payload),
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
      details?: string;
    };
    throw new Error(body.error || body.details || `Failed to create PCR (${response.status})`);
  }

  const data = (await response.json()) as { submission?: PcrSubmission };
  if (!data.submission) {
    throw new Error('PCR was created, but no submission was returned.');
  }
  return data.submission;
}

export async function fetchPcrSubmissionHistory(submissionId: string): Promise<PcrSubmissionHistory[]> {
  const response = await fetch(`${API_URL}/pcr/submissions/${submissionId}/history`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string; details?: string };
    const combined = [body.error, body.details].filter(Boolean).join(': ');
    throw new Error(combined || `Failed to load PCR history (${response.status})`);
  }

  const data = (await response.json()) as { history?: PcrSubmissionHistory[] };
  return data.history ?? [];
}

/** PDF audit pack for ministry / legal use (history, notes, emails, Work ID, closure). */
export async function downloadPcrAuditReportPdf(submissionId: string | number): Promise<Blob> {
  const response = await fetch(`${API_URL}/pcr/submissions/${submissionId}/audit-report-pdf`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/pdf' },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string; details?: string };
    const combined = [body.error, body.details].filter(Boolean).join(': ');
    throw new Error(combined || `Failed to download audit report (${response.status})`);
  }

  return response.blob();
}

// ─── PCR Grouping / Similarity Feature ──────────────────────────────────────

export interface PcrGroupMember {
  id: number;
  reportId: string | null;
  receivedAt: string;
  status: string;
  responseTimeBucket: string | null;
  serviceArea: string | null;
  community: string | null;
  foremanAreaName: string | null;
  roadName: string | null;
  reportType: string | null;
  activityType: string | null;
  userName: string | null;
  message: string | null;
  workIdId: number | null;
  linkedWorkIdentificationId: string | null;
  similarityScore: number | null;
  reasonSummary: string | null;
}

export interface PcrSimilarityGroup {
  groupKey: string;
  label: string;
  reasonTags: string[];
  members: PcrGroupMember[];
}

export interface PcrGroupedResult {
  groups: PcrSimilarityGroup[];
  ungrouped: PcrGroupMember[];
}

export interface BulkPcrActionResult {
  successCount: number;
  errors: string[];
  workIdentificationId?: string | null;
}

export async function fetchPcrGroupSuggestions(params: PcrListParams): Promise<PcrGroupedResult> {
  const query = toQueryWithMultiFilters(
    {
      from: params.from,
      to: params.to,
      serviceArea: params.serviceArea,
      road: params.road,
      search: params.search,
    },
    {
      serviceAreaId: params.serviceAreaId,
      foremanAreaId: params.foremanAreaId,
      roadAssetId: params.roadAssetId,
      reportTypeBuckets: params.reportTypeBuckets,
      statuses: params.statuses,
    },
  );
  const response = await fetch(`${API_URL}/pcr/group-suggestions${query}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Failed to load PCR group suggestions (${response.status})`);
  }
  return (await response.json()) as PcrGroupedResult;
}

export async function rejectPcrGroupSuggestion(pcrId: number, targetId: number): Promise<void> {
  const response = await fetch(`${API_URL}/pcr/submissions/${pcrId}/group-suggestion/${targetId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Failed to remove from group (${response.status})`);
  }
}

export async function generatePcrGroupSuggestions(): Promise<{ processed: number }> {
  const response = await fetch(`${API_URL}/pcr/group-suggestions/generate`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Failed to generate PCR group suggestions (${response.status})`);
  }
  return (await response.json()) as { processed: number };
}

export async function bulkClosePcrs(ids: number[], note?: string): Promise<BulkPcrActionResult> {
  const response = await fetch(`${API_URL}/pcr/submissions/bulk-close`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ ids, note }),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Bulk close failed (${response.status})`);
  }
  return (await response.json()) as BulkPcrActionResult;
}

export async function bulkAddNoteToPcrs(ids: number[], note: string): Promise<BulkPcrActionResult> {
  const response = await fetch(`${API_URL}/pcr/submissions/bulk-add-note`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ ids, note }),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Bulk add note failed (${response.status})`);
  }
  return (await response.json()) as BulkPcrActionResult;
}

export async function bulkLinkWorkId(ids: number[], workIdId: number): Promise<BulkPcrActionResult> {
  const response = await fetch(`${API_URL}/pcr/submissions/bulk-link-work-id`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ ids, workIdId }),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Bulk link Work ID failed (${response.status})`);
  }
  return (await response.json()) as BulkPcrActionResult;
}

export async function postPcrHistoryComment(
  submissionId: string,
  payload: PostPcrHistoryCommentPayload,
): Promise<PcrSubmissionHistory> {
  const response = await fetch(`${API_URL}/pcr/submissions/${submissionId}/history-comment`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      note: payload.note,
      kind: payload.kind,
      callerContactedBy: payload.callerContactedBy,
      contactedAt: payload.contactedAt,
      contractorsRepresentative: payload.contractorsRepresentative,
      isFirstContact: payload.isFirstContact,
      firstContactAt: payload.firstContactAt,
    }),
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string; details?: string };
    const combined = [body.error, body.details].filter(Boolean).join(': ');
    throw new Error(combined || `Failed to save PCR note (${response.status})`);
  }

  const data = (await response.json()) as { entry?: PcrSubmissionHistory };
  if (!data.entry) {
    throw new Error('Note was saved, but no history entry was returned.');
  }
  return data.entry;
}
